import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Mail, Send, Sparkles, X } from 'lucide-react';
import { invokeEdgeFunction } from '../../lib/invokeEdgeFunction';
import {
  describeInviteMessageContext,
  generateSubscriberInviteMessage,
  INVITE_MESSAGE_TONES,
  type InviteMessageTone,
} from '../../lib/generateSubscriberInviteMessage';
import { toast } from 'sonner';
import ConfirmModal from '../Common/ConfirmModal';

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

function newsletterUrlFromEnv(): string {
  const fromEnv = import.meta.env.VITE_NEWSLETTER_URL?.trim();
  if (fromEnv) return fromEnv;
  return 'https://stephansmith.me/subscribe';
}

function subscribeUrlForEmail(email: string): string {
  const base = newsletterUrlFromEnv().replace(/\/$/, '');
  const normalized = email.trim().toLowerCase();
  if (!normalized) return base;
  const joiner = base.includes('?') ? '&' : '?';
  return `${base}${joiner}email=${encodeURIComponent(normalized)}`;
}

function defaultSubject(): string {
  return "You're invited — Stephan's newsletter";
}

function defaultPersonalMessage(recipientName: string | null | undefined): string {
  const greeting = recipientName?.trim() ? `Hi ${recipientName.trim()},` : 'Hi,';
  return `${greeting}

I added you to my list and wanted to invite you to subscribe to my weekly newsletter — plain-language notes for founders and CEOs on insights, playbook picks, and build-in-public.

Tap Subscribe below to pick your topics and confirm your email. It's free.`;
}

export interface SendSubscriberInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSent?: () => void;
  toEmail: string;
  /** Required for AI draft — CRM subscriber id. */
  subscriberId?: string | null;
  /** Nickname or first name for the "Hi …," greeting — not the full legal name. */
  recipientName: string | null | undefined;
  /** When set, show prior send info and confirm before resending. */
  previousSentAt?: string | null;
  previousSendCount?: number;
}

export default function SendSubscriberInviteModal({
  isOpen,
  onClose,
  onSent,
  toEmail,
  subscriberId = null,
  recipientName,
  previousSentAt = null,
  previousSendCount = 0,
}: SendSubscriberInviteModalProps) {
  const [subject, setSubject] = useState(defaultSubject);
  const [personalMessage, setPersonalMessage] = useState('');
  const [subscribeUrl, setSubscribeUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [tonePickerOpen, setTonePickerOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiContextHint, setAiContextHint] = useState<string | null>(null);

  const canUseAi = Boolean(subscriberId?.trim());

  const isResend = previousSendCount > 0;

  useEffect(() => {
    if (!isOpen) return;
    setSubscribeUrl(subscribeUrlForEmail(toEmail));
    setSubject(defaultSubject());
    setPersonalMessage(defaultPersonalMessage(recipientName));
    setResendConfirmOpen(false);
    setTonePickerOpen(false);
    setAiContextHint(null);
  }, [isOpen, recipientName, toEmail]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, sending]);

  const handleSend = useCallback(async () => {
    const email = toEmail.trim();
    const message = personalMessage.trim();
    if (!email) return;
    if (!message) {
      toast.error('Write a personal message before sending.');
      return;
    }

    setSending(true);
    try {
      await invokeEdgeFunction<{ success?: boolean; error?: string }>(
        'llc-send-subscriber-invite',
        {
          email,
          subject: subject.trim() || defaultSubject(),
          personal_message: message,
        },
      );
      toast.success(`Invite sent to ${email}`);
      onSent?.();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not send invite');
    } finally {
      setSending(false);
    }
  }, [toEmail, personalMessage, subject, onClose, onSent]);

  const requestSend = useCallback(() => {
    if (isResend) {
      setResendConfirmOpen(true);
      return;
    }
    void handleSend();
  }, [handleSend, isResend]);

  const runAiDraft = useCallback(
    async (tone: InviteMessageTone) => {
      const sid = subscriberId?.trim();
      if (!sid) {
        toast.error('Save this connection before using AI draft.');
        return;
      }
      setTonePickerOpen(false);
      setGenerating(true);
      setAiContextHint(null);
      try {
        const result = await generateSubscriberInviteMessage(sid, tone, recipientName);
        setSubject(result.subject);
        setPersonalMessage(result.personal_message);
        setAiContextHint(describeInviteMessageContext(result.context_used));
        toast.success('Draft ready — edit before sending.');
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Could not generate draft');
      } finally {
        setGenerating(false);
      }
    },
    [subscriberId, recipientName],
  );

  if (!isOpen) return null;

  return (
    <>
      <ConfirmModal
        isOpen={resendConfirmOpen}
        title="Send another invite?"
        description={
          <div className="space-y-2">
            <p>
              You already sent a newsletter invite to{' '}
              <span className="font-mono text-xs break-all">{toEmail.trim()}</span>
              {previousSentAt ? (
                <>
                  {' '}
                  on <span className="font-medium">{formatWhen(previousSentAt)}</span>
                </>
              ) : null}
              {previousSendCount > 1 ? (
                <>
                  {' '}
                  ({previousSendCount} sends total)
                </>
              ) : null}
              .
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Only send again if they did not receive it or asked for a fresh link. They are still
              CRM-only until they subscribe.
            </p>
          </div>
        }
        confirmLabel="Yes, send again"
        cancelLabel="Cancel"
        busy={sending}
        onConfirm={() => {
          setResendConfirmOpen(false);
          void handleSend();
        }}
        onClose={() => {
          if (!sending) setResendConfirmOpen(false);
        }}
      />

      <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      role="presentation"
    >
      <div
        className="flex w-full max-w-2xl flex-col overflow-hidden bg-white shadow-xl dark:bg-gray-800 min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-subscriber-invite-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4 dark:border-gray-700">
          <div className="flex min-w-0 items-center gap-3">
            <div className="shrink-0 rounded-lg bg-emerald-500/10 p-2 text-emerald-600 dark:text-emerald-400">
              <Mail className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <h2
                id="send-subscriber-invite-title"
                className="text-lg font-semibold text-gray-900 dark:text-white"
              >
                Send newsletter invite
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Postmark template <code className="rounded bg-gray-100 px-1 dark:bg-gray-900">invite</code>{' '}
                on layout <code className="rounded bg-gray-100 px-1 dark:bg-gray-900">base-note</code> — plain
                note, not newsletter marketing.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6">
          {isResend ? (
            <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <p>
                Prior invite{' '}
                {previousSentAt ? (
                  <>
                    sent <span className="font-medium">{formatWhen(previousSentAt)}</span>
                  </>
                ) : (
                  'already on file'
                )}
                . Sending again will deliver a second email.
              </p>
            </div>
          ) : null}

          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              To
            </p>
            <p className="mt-1 break-all font-mono text-sm text-gray-900 dark:text-gray-100">
              {toEmail.trim() || '—'}
            </p>
          </div>

          <div>
            <label
              htmlFor="invite-subscribe-url"
              className="text-xs font-medium text-gray-700 dark:text-gray-300"
            >
              Subscribe link (email button)
            </label>
            <input
              id="invite-subscribe-url"
              type="url"
              readOnly
              value={subscribeUrl}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-300"
            />
          </div>

          <div>
            <label
              htmlFor="invite-subject"
              className="text-xs font-medium text-gray-700 dark:text-gray-300"
            >
              Subject
            </label>
            <input
              id="invite-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label
                htmlFor="invite-personal-message"
                className="text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                Personal message
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    if (!canUseAi) {
                      toast.error('Save this connection before using AI draft.');
                      return;
                    }
                    setTonePickerOpen((open) => !open);
                  }}
                  disabled={generating || sending}
                  title={
                    canUseAi
                      ? 'Draft from last transcript, custom fields, and email history'
                      : 'Save the connection first to enable AI draft'
                  }
                  className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-xs font-medium text-violet-800 hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-100 dark:hover:bg-violet-900/50"
                >
                  {generating ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" aria-hidden />
                  )}
                  {generating ? 'Drafting…' : 'AI draft'}
                </button>
                {tonePickerOpen ? (
                  <>
                    <button
                      type="button"
                      className="fixed inset-0 z-10 cursor-default"
                      aria-label="Close tone picker"
                      onClick={() => setTonePickerOpen(false)}
                    />
                    <div
                      className="absolute right-0 z-20 mt-1.5 w-72 rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800"
                      role="menu"
                    >
                      <p className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Choose tone
                      </p>
                      {INVITE_MESSAGE_TONES.map((tone) => (
                        <button
                          key={tone.id}
                          type="button"
                          role="menuitem"
                          onClick={() => void runAiDraft(tone.id)}
                          className="w-full rounded-lg px-2 py-2 text-left hover:bg-violet-50 dark:hover:bg-violet-950/40"
                        >
                          <span className="block text-sm font-medium text-gray-900 dark:text-white">
                            {tone.label}
                          </span>
                          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                            {tone.description}
                          </span>
                        </button>
                      ))}
                      <p className="mt-1 border-t border-gray-100 px-2 pt-2 text-[11px] leading-snug text-gray-500 dark:border-gray-700 dark:text-gray-400">
                        Pulls your last transcript with this contact, custom fields, and prior
                        sends.
                      </p>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              This appears in the email above the Subscribe button.
            </p>
            {aiContextHint ? (
              <p className="mt-1.5 text-xs text-violet-700 dark:text-violet-300">{aiContextHint}</p>
            ) : null}
            <textarea
              id="invite-personal-message"
              value={personalMessage}
              onChange={(e) => {
                setPersonalMessage(e.target.value);
                if (aiContextHint) setAiContextHint(null);
              }}
              disabled={generating}
              rows={12}
              className="mt-2 min-h-[12rem] w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm leading-relaxed text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 bg-gray-50/90 px-4 py-3 sm:px-6 dark:border-gray-700 dark:bg-gray-900/50">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void requestSend()}
            disabled={sending || !toEmail.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
            ) : (
              <Send className="h-4 w-4 shrink-0" aria-hidden />
            )}
            {sending ? 'Sending…' : isResend ? 'Send again' : 'Send invite email'}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
